import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import PropertyCard from './PropertyCard';
import { useCallback, useContext } from 'react';
import { fetchCompoundSearchResults } from '../../../../../new-lib/apis';
import NawyLoadingIndicator from '../../../../../new-components/NawyLoadingIndicator';
import NoResultsIndicator from '../../../../../new-components/NoResultsIndicator';
import ErrorComponent from '../../../../../new-components/ui/ErrorComponent';
import { mapSearchFiltersKeysToSearchApiKeys } from '../../../../../helpers/search-filters';
import { SearchFiltersTypes } from '../../../../../reducers/search-filters/reducer';
import {
  SearchContext,
  SearchContextInterface
} from '../../../../search-new/context/SearchContext';
import SoldOutSection from '../../../../entity/components/SoldOutSection';
import { Compound } from '../../../../../new-lib/types/entity';
import { CompoundSummaryDto } from '../../../dtos';

interface CompoundSearchResultsProps {
  compound: Compound | CompoundSummaryDto;
  activeTab: string;
  propertiesImagesSize?: 'default' | 'vLow' | 'low' | 'medium' | 'high';
}

const CompoundSearchResults = ({
  compound,
  activeTab,
  propertiesImagesSize
}: CompoundSearchResultsProps) => {
  const { locale } = useRouter();

  const selectedCompound = useSelector(
    (state: any) => state.map.selectedCompound
  );

  const { searchFiltersState, appliedFiltersCount } = useContext(
    SearchContext
  ) as SearchContextInterface;

  const getCompoundSearchBody = () => {
    let body = { ...searchFiltersState.searchBody };
    body[mapSearchFiltersKeysToSearchApiKeys(SearchFiltersTypes.COMPOUNDS)] = [
      selectedCompound?.id
    ];
    body[mapSearchFiltersKeysToSearchApiKeys(SearchFiltersTypes.CATEGORY)] =
      'property';
    return body;
  };
  const {
    data: compoundProperties,
    isLoading,
    isError
  } = useQuery({
    queryKey: [
      `compound-properties-${selectedCompound?.id}-${activeTab}`,
      selectedCompound,
      searchFiltersState
    ],
    queryFn: () => fetchCompoundSearchResults(getCompoundSearchBody(), locale),
    staleTime: Infinity
  });

  const renderSearchResults = useCallback(() => {
    if (isLoading) {
      return <NawyLoadingIndicator />;
    }
    if (isError || !compoundProperties) {
      return <ErrorComponent />;
    }
    if (compoundProperties.data.values.length === 0) {
      return appliedFiltersCount > 0 ? (
        <NoResultsIndicator isResetButton={true} />
      ) : (
        <SoldOutSection
          compound={{
            id: compound?.id,
            name: compound?.name,
            nawy_organization_id: compound?.nawyOrganizationId,
            area: { id: compound?.areaId.toString(), name: compound?.areaName }
          }}
        />
      );
    }

    return compoundProperties?.data?.values.map((property: any) => (
      <PropertyCard key={property?.id} property={property} imageSize="high" />
    ));
  }, [compoundProperties, activeTab, isLoading, isError, searchFiltersState]);

  return <Container>{renderSearchResults()}</Container>;
};

const Container = styled.div`
  min-height: 300px;
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 16px;
`;
export default CompoundSearchResults;
